import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { ClipboardDocumentCheckIcon, XMarkIcon, ChevronRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { listAdvisorReviews } from '../../services/appointmentsApi';
import { hasReviewAccess, reviewIdentity, isPendingReview, reviewErrorKey, formatReviewDate } from './advisorReview';
import { formatBookingExtras } from './bookingExtras';
import DatosDisputeMarker from './DatosDisputeMarker';
import { formatDatosUpdates } from './datosUpdates';
import './advisorReview.css';

export default function AdvisorReviewQueue({ user, revision, onOpen, open, onOpenChange }) {
  const identity = reviewIdentity(user);
  if (!identity) return null;
  return <QueueSession key={JSON.stringify([identity, revision])} identity={identity}
    onOpen={onOpen} open={open} onOpenChange={onOpenChange} />;
}

function QueueSession({ identity, onOpen, open: controlledOpen, onOpenChange }) {
  const { t, i18n } = useTranslation();
  const label = (key) => t('advisorReview.' + key);
  const user = useMemo(() => JSON.parse(identity), [identity]);
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = onOpenChange || setLocalOpen;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [cursor, setCursor] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(''); setPage(null);
    listAdvisorReviews({ limit: 25, cursor }).then(({ data }) => {
      if (alive) setPage(data);
    }).catch((err) => { if (alive) setError(reviewErrorKey(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [identity, cursor, refresh]);

  const items = (page?.items || []).filter((row) => isPendingReview(row) && hasReviewAccess(user, row));
  return <>
    <button type="button" className="review-trigger" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
      <ClipboardDocumentCheckIcon className="h-5 w-5" />{label('reviews')}
      <span className="review-count">{loading ? '…' : error ? '!' : page?.count || 0}</span>
    </button>
    <Dialog open={open} onClose={() => setOpen(false)} className="relative z-40">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto p-4 sm:p-12">
        <Dialog.Panel className="review-list mx-auto max-w-2xl">
          <div className="review-list-heading">
            <div>
              <Dialog.Title className="text-xl font-semibold">{label('queue')}</Dialog.Title>
              <p className="review-muted mt-1 text-sm">{label('chooseReview')}</p>
            </div>
            <button type="button" aria-label={label('closeList')} className="review-icon" onClick={() => setOpen(false)}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="review-list-summary">
            <span role="status">{!loading && !error ? t('advisorReview.queueCount', { count: page?.count || 0 }) : ''}</span>
            <button type="button" className="inline-flex items-center gap-2" disabled={loading}
              onClick={() => { setCursor(null); setHistory([]); setRefresh((n) => n + 1); }}>
              <ArrowPathIcon className="h-4 w-4" />{label('refreshQueue')}
            </button>
          </div>
          {loading ? <p className="p-6" role="status">{label('loadingQueue')}</p>
            : error ? <p role="alert" className="p-6">{label(error)}</p>
              : items.length === 0 ? <div className="p-10 text-center">
                <ClipboardDocumentCheckIcon className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
                <p className="font-medium">{label('allClear')}</p>
                <p className="review-muted text-sm mt-1">{label('empty')}</p>
              </div>
                : <ul>{items.map((row) => <li key={row.appointment_id}>
                  <button type="button" className="review-list-row" onClick={() => { setOpen(false); onOpen(row.appointment_id); }}>
                    <div className="review-avatar" aria-hidden="true">
                      {row.customer_name?.split(' ').slice(0, 2).map((name) => name[0]).join('')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{row.customer_name || label('unavailable')}</div>
                      <div className="review-muted mt-1 text-sm">{[row.service, row.vehicle].filter(Boolean).join(' · ')}</div>
                      <div className="review-muted mt-2 text-xs">
                        {formatReviewDate(row.start_datetime, i18n?.language) || label('unavailable')} · {row.sucursal || row.location_id}
                      </div>
                      {/* taxi-factura-oct20: optional taxi / factura answers, only when answered */}
                      {formatBookingExtras(row, (k, f) => t(k, f)) && <div className="review-muted mt-1 text-xs" data-testid="review-row-extras">
                        {formatBookingExtras(row, (k, f) => t(k, f))}
                      </div>}
                      {/* datos step B oct21: "Cliente reporta datos incorrectos: teléfono → 993…" (advisor fixes it in B.PRO) */}
                      <DatosDisputeMarker row={row} compact testId="review-row-datos-dispute" />
                      {/* td453: datos corrected by the customer after the datos message */}
                      {formatDatosUpdates(row, (k, f) => t(k, f)) && <div className="review-muted mt-1 text-xs" data-testid="review-row-datos-updates">
                        {formatDatosUpdates(row, (k, f) => t(k, f))}
                      </div>}
                    </div>
                    <ChevronRightIcon className="h-5 w-5 shrink-0 review-muted" />
                  </button>
                </li>)}</ul>}
          {!loading && !error && <nav aria-label={label('pagination')} className="flex justify-between p-4">
            <button type="button" disabled={!history.length} onClick={() => {
              setCursor(history[history.length - 1]); setHistory((h) => h.slice(0, -1));
            }}>{label('previous')}</button>
            <button type="button" disabled={!page?.next_cursor} onClick={() => {
              setHistory((h) => [...h, cursor]); setCursor(page.next_cursor);
            }}>{label('next')}</button>
          </nav>}
        </Dialog.Panel>
      </div>
    </Dialog>
  </>;
}
